import React, { useRef, useEffect } from 'react';

const GOLD = '197,160,89';

const LOGO_CDN = 'https://cdn.jsdelivr.net/gh/spothq/cryptocurrency-icons@0.18.1/128/color';
const TOP10 = ['btc', 'eth', 'usdt', 'bnb', 'sol', 'xrp', 'usdc', 'ada', 'doge', 'trx'];

const FloatingLogos = () => {
  const wrapRef = useRef(null);

  useEffect(() => {
    const wrap = wrapRef.current;
    const nodes = Array.from(wrap.querySelectorAll('img'));
    const state = nodes.map((el, i) => ({
      el,
      x: Math.random() * 100,
      y: 8 + Math.random() * 80,
      vx: (Math.random() - 0.5) * 0.028,
      vy: (Math.random() - 0.5) * 0.02,
      phase: Math.random() * Math.PI * 2,
    }));
    let raf, t = 0;
    const step = () => {
      t += 0.008;
      const H = wrap.offsetHeight || 600;
      for (const s of state) {
        s.x += s.vx; s.y += s.vy;
        if (s.x < -6) s.x = 106; if (s.x > 106) s.x = -6;
        if (s.y < 2) { s.y = 2; s.vy = Math.abs(s.vy); }
        if (s.y > 88) { s.y = 88; s.vy = -Math.abs(s.vy); }
        const bob = Math.sin(t * 2 + s.phase) * 6;
        s.el.style.transform = `translate(${s.x}vw, ${(s.y / 100) * H + bob}px)`;
      }
      raf = requestAnimationFrame(step);
    };
    step();
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div ref={wrapRef} className="absolute inset-0" data-testid="hero-floating-logos">
      {TOP10.map((s, i) => (
        <img
          key={s}
          src={`${LOGO_CDN}/${s}.png`}
          alt=""
          loading="lazy"
          className="absolute top-0 left-0 rounded-full"
          style={{
            width: 34 + (i % 4) * 10,
            opacity: 0.4 + (i % 3) * 0.09,
            filter: 'drop-shadow(0 0 14px rgba(197,160,89,0.35)) saturate(0.9)',
            willChange: 'transform',
          }}
        />
      ))}
    </div>
  );
};

const Candles3D = () => {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext('2d');
    let raf, w, h;
    const DPR = Math.min(window.devicePixelRatio || 1, 1.5);
    const SPACING = 30, BODY = 16, DEPTH = 6, SPEED = 0.35;

    const resize = () => {
      w = canvas.offsetWidth; h = canvas.offsetHeight;
      canvas.width = w * DPR; canvas.height = h * DPR;
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);

    let price = 0.5, mom = 0, vol = 0.03;
    const newCandle = () => {
      mom = mom * 0.8 + (Math.random() - 0.5) * 0.045;           // trending momentum
      vol = Math.max(0.012, Math.min(0.07, vol * 0.75 + Math.random() * 0.02));  // volatility clustering
      const o = price;
      let c = o + mom + (Math.random() - 0.5) * vol;
      c = Math.min(0.88, Math.max(0.22, c));
      if (c <= 0.23 || c >= 0.87) mom = -mom * 0.6;              // bounce off range
      price = c;
      const wickBias = Math.random();
      const hi = Math.max(o, c) + Math.random() * vol * (wickBias > 0.85 ? 1.6 : 0.5);
      const lo = Math.min(o, c) - Math.random() * vol * (wickBias < 0.15 ? 1.6 : 0.5);
      const volume = Math.min(1, Math.abs(c - o) * 9 + Math.random() * 0.35);
      return { o, c, hi, lo, volume };
    };
    const count = Math.ceil(2200 / SPACING) + 2;
    const candles = Array.from({ length: count }, newCandle);
    let offset = 0;

    const Y = (v) => h * 0.86 * (1 - v);

    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      offset += SPEED;
      if (offset >= SPACING) { offset -= SPACING; candles.shift(); candles.push(newCandle()); }
      for (let i = 0; i < candles.length; i++) {
        const k = candles[i];
        const x = i * SPACING - offset;
        if (x > w + SPACING) break;
        const up = k.c >= k.o;
        const col = up ? '22,199,132' : '234,57,67';   // exchange-style green/red
        const top = Y(Math.max(k.o, k.c));
        const bot = Y(Math.min(k.o, k.c));
        const bodyH = Math.max(2, bot - top);
        // volume bar
        ctx.fillStyle = `rgba(${col},0.18)`;
        ctx.fillRect(x + 2, h - k.volume * h * 0.12, BODY - 4, k.volume * h * 0.12);
        // wick (colored like real charts)
        ctx.strokeStyle = `rgba(${col},0.75)`;
        ctx.lineWidth = 1.4;
        ctx.beginPath();
        ctx.moveTo(x + BODY / 2, Y(k.hi));
        ctx.lineTo(x + BODY / 2, Y(k.lo));
        ctx.stroke();
        // 3D extrusion: side face
        ctx.fillStyle = `rgba(${col},0.30)`;
        ctx.beginPath();
        ctx.moveTo(x + BODY, top);
        ctx.lineTo(x + BODY + DEPTH, top - DEPTH);
        ctx.lineTo(x + BODY + DEPTH, top - DEPTH + bodyH);
        ctx.lineTo(x + BODY, top + bodyH);
        ctx.closePath();
        ctx.fill();
        // top face
        ctx.fillStyle = `rgba(${col},0.5)`;
        ctx.beginPath();
        ctx.moveTo(x, top);
        ctx.lineTo(x + DEPTH, top - DEPTH);
        ctx.lineTo(x + BODY + DEPTH, top - DEPTH);
        ctx.lineTo(x + BODY, top);
        ctx.closePath();
        ctx.fill();
        // front face: vertical gradient for realism
        const grad = ctx.createLinearGradient(0, top, 0, top + bodyH);
        grad.addColorStop(0, `rgba(${col},0.85)`);
        grad.addColorStop(1, `rgba(${col},0.45)`);
        ctx.fillStyle = grad;
        ctx.fillRect(x, top, BODY, bodyH);
        ctx.strokeStyle = `rgba(${col},0.9)`;
        ctx.lineWidth = 0.8;
        ctx.strokeRect(x, top, BODY, bodyH);
      }
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', resize); };
  }, []);

  return (
    <div className="absolute inset-x-0" style={{
      bottom: '4%', height: '52%',
      transform: 'perspective(1100px) rotateX(38deg) scale(1.12)',
      transformOrigin: 'center bottom',
      maskImage: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.7) 18%, rgba(0,0,0,0.7) 82%, transparent 100%), linear-gradient(to top, rgba(0,0,0,0.9) 30%, transparent 95%)',
      WebkitMaskImage: 'linear-gradient(to top, rgba(0,0,0,0.85) 25%, transparent 95%)',
      opacity: 0.68,
    }} data-testid="hero-3d-candles">
      <canvas ref={ref} className="w-full h-full" />
    </div>
  );
};

export const HeroBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let raf, w, h;
    const DPR = Math.min(window.devicePixelRatio || 1, 1.5);

    const resize = () => {
      w = canvas.offsetWidth;
      h = canvas.offsetHeight;
      canvas.width = w * DPR;
      canvas.height = h * DPR;
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);

    const N = Math.min(90, Math.floor((w * h) / 22000));
    const pts = Array.from({ length: N }, () => {
      const z = 0.35 + Math.random() * 0.65; // depth: far = small & dim
      return {
        x: Math.random() * w, y: Math.random() * h, z,
        vx: (Math.random() - 0.5) * 0.22 * z,
        vy: (Math.random() - 0.5) * 0.22 * z,
      };
    });

    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      for (let i = 0; i < pts.length; i++) {
        const p = pts[i];
        p.x += p.vx; p.y += p.vy;
        if (p.x < -20) p.x = w + 20; if (p.x > w + 20) p.x = -20;
        if (p.y < -20) p.y = h + 20; if (p.y > h + 20) p.y = -20;
        for (let j = i + 1; j < pts.length; j++) {
          const q = pts[j];
          const dx = p.x - q.x, dy = p.y - q.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < 15000) {
            const a = (1 - d2 / 15000) * 0.25 * Math.min(p.z, q.z);
            ctx.strokeStyle = `rgba(${GOLD},${a})`;
            ctx.lineWidth = 0.7;
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(q.x, q.y);
            ctx.stroke();
          }
        }
        ctx.fillStyle = `rgba(${GOLD},${0.25 + p.z * 0.45})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, 0.8 + p.z * 1.4, 0, Math.PI * 2);
        ctx.fill();
      }
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', resize); };
  }, []);

  return (
    <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none" data-testid="hero-3d-background">
      {/* Base depth gradient */}
      <div className="absolute inset-0" style={{
        background: 'radial-gradient(ellipse 80% 60% at 50% -10%, rgba(197,160,89,0.13), transparent 60%), radial-gradient(ellipse 60% 50% at 85% 90%, rgba(16,185,129,0.05), transparent 65%), radial-gradient(ellipse 55% 45% at 12% 80%, rgba(197,160,89,0.06), transparent 65%)',
      }} />

      {/* 3D perspective grid floor */}
      <div className="absolute left-1/2 -translate-x-1/2 bottom-0" style={{
        width: '240%', height: '62%',
        backgroundImage: `linear-gradient(rgba(${GOLD},0.30) 1px, transparent 1px), linear-gradient(90deg, rgba(${GOLD},0.30) 1px, transparent 1px)`,
        backgroundSize: '56px 56px',
        transform: 'perspective(620px) rotateX(63deg)',
        transformOrigin: 'center top',
        maskImage: 'linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.85) 42%, transparent 96%)',
        WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.85) 42%, transparent 96%)',
        animation: 'dggGridDrift 16s linear infinite',
      }} />

      {/* 3D running candlestick plane */}
      <Candles3D />

      {/* Wandering top-10 crypto logos */}
      <FloatingLogos />

      {/* Horizon glow above the grid */}
      <div className="absolute left-0 right-0" style={{
        bottom: '30%', height: '22%',
        background: 'radial-gradient(ellipse 55% 100% at 50% 100%, rgba(197,160,89,0.22), transparent 70%)',
        filter: 'blur(8px)',
      }} />

      {/* Particle constellation */}
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" style={{ opacity: 0.9 }} />

      {/* Grain texture */}
      <div className="absolute inset-0" style={{
        opacity: 0.05, mixBlendMode: 'overlay',
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
      }} />

      {/* Bottom fade into page background */}
      <div className="absolute inset-x-0 bottom-0 h-28" style={{ background: 'linear-gradient(to bottom, transparent, #0A0A0A)' }} />

      <style>{`
        @keyframes dggGridDrift {
          from { background-position: 0 0, 0 0; }
          to { background-position: 0 56px, 0 0; }
        }
      `}</style>
    </div>
  );
};

export default HeroBackground;
